import { z } from 'zod';
import { assignmentStatusSchema } from './scheduling.js';

/**
 * O ciclo de quem serve (M08 e M09): receber a convocação, confirmar em um
 * toque, recusar com motivo, pedir troca e chegar no dia.
 */

/** Motivos de recusa que a igreja mais ouve, para não obrigar a escrever. */
export const DECLINE_REASONS = ['TRAVEL', 'WORK', 'HEALTH', 'FAMILY', 'STUDY', 'OTHER'] as const;
export const declineReasonSchema = z.enum(DECLINE_REASONS);
export type DeclineReason = z.infer<typeof declineReasonSchema>;

export const DECLINE_REASON_LABEL: Record<DeclineReason, string> = {
  TRAVEL: 'Vou viajar',
  WORK: 'Compromisso de trabalho',
  HEALTH: 'Questão de saúde',
  FAMILY: 'Compromisso de família',
  STUDY: 'Estudo ou prova',
  OTHER: 'Outro motivo',
};

/** Uma escala como quem serve enxerga: o que é, quando e o que se espera dela. */
export const myAssignmentSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  eventTitle: z.string(),
  eventLocation: z.string().nullable(),
  localDate: z.string(),
  localTime: z.string(),
  weekday: z.number().int(),
  startsAt: z.string().datetime(),
  functionName: z.string(),
  ministryName: z.string(),
  ministryColor: z.string(),
  status: assignmentStatusSchema,
  notes: z.string().nullable(),
  declineReason: z.string().nullable(),
  /** Até quando a igreja espera a resposta. */
  confirmBy: z.string().datetime().nullable(),
  /** A resposta está atrasada — é o que o painel destaca. */
  atrasada: z.boolean(),
  /** Quem mais está escalado no mesmo evento (a equipe do dia). */
  equipe: z.array(z.object({ nome: z.string(), funcao: z.string(), status: assignmentStatusSchema })),
  podeConfirmar: z.boolean(),
  podeRecusar: z.boolean(),
  podePedirTroca: z.boolean(),
  podeFazerCheckin: z.boolean(),
  checkedInAt: z.string().datetime().nullable(),
});
export type MyAssignment = z.infer<typeof myAssignmentSchema>;

export const respondAssignmentSchema = z
  .object({
    resposta: z.enum(['CONFIRMAR', 'RECUSAR']),
    motivo: declineReasonSchema.optional(),
    observacao: z.string().trim().max(300).nullish(),
  })
  .superRefine((valor, contexto) => {
    if (valor.resposta === 'RECUSAR' && !valor.motivo) {
      contexto.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['motivo'],
        message: 'Diga o motivo — é o que permite achar um substituto a tempo',
      });
    }
  });
export type RespondAssignmentInput = z.infer<typeof respondAssignmentSchema>;

// ── Substituição ─────────────────────────────────────────────────────────────

export const SUBSTITUTION_INVITE_STATUS = [
  'PENDING',
  'ACCEPTED',
  'DECLINED',
  'EXPIRED',
  'CANCELLED',
] as const;
export const substitutionInviteStatusSchema = z.enum(SUBSTITUTION_INVITE_STATUS);
export type SubstitutionInviteStatus = z.infer<typeof substitutionInviteStatusSchema>;

export const SUBSTITUTION_INVITE_STATUS_LABEL: Record<SubstitutionInviteStatus, string> = {
  PENDING: 'Aguardando resposta',
  ACCEPTED: 'Aceito',
  DECLINED: 'Recusado',
  EXPIRED: 'Sem resposta a tempo',
  CANCELLED: 'Cancelado',
};

export const substitutionInviteSchema = z.object({
  id: z.string().uuid(),
  assignmentId: z.string().uuid(),
  eventId: z.string().uuid(),
  eventTitle: z.string(),
  localDate: z.string(),
  localTime: z.string(),
  functionName: z.string(),
  ministryName: z.string(),
  /** Quem deixou a vaga. */
  deQuem: z.string(),
  personId: z.string().uuid(),
  personName: z.string(),
  status: substitutionInviteStatusSchema,
  rank: z.number().int(),
  sentAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  respondedAt: z.string().datetime().nullable(),
});
export type SubstitutionInvite = z.infer<typeof substitutionInviteSchema>;

export const openSubstitutionSchema = z.object({
  /** Quem convidar. Vazio = os mais bem colocados na avaliação de candidatos. */
  personIds: z.array(z.string().uuid()).max(20).optional(),
  quantidade: z.coerce.number().int().min(1).max(10).default(3),
});
export type OpenSubstitutionInput = z.infer<typeof openSubstitutionSchema>;

export const respondInviteSchema = z
  .object({
    resposta: z.enum(['ACEITAR', 'RECUSAR']),
    motivo: z.string().trim().max(300).nullish(),
  })
  .strict();
export type RespondInviteInput = z.infer<typeof respondInviteSchema>;

// ── Troca ────────────────────────────────────────────────────────────────────

export const SWAP_STATUS = [
  'OPEN',
  'ACCEPTED',
  'APPROVED',
  'REJECTED',
  'CANCELLED',
  'EXPIRED',
] as const;
export const swapStatusSchema = z.enum(SWAP_STATUS);
export type SwapStatus = z.infer<typeof swapStatusSchema>;

export const SWAP_STATUS_LABEL: Record<SwapStatus, string> = {
  OPEN: 'Aguardando colega',
  ACCEPTED: 'Aguardando o líder',
  APPROVED: 'Troca feita',
  REJECTED: 'Recusada pelo líder',
  CANCELLED: 'Cancelada',
  EXPIRED: 'Sem resposta a tempo',
};

export const SWAP_KIND = ['GIVE_AWAY', 'EXCHANGE'] as const;
export const swapKindSchema = z.enum(SWAP_KIND);
export type SwapKind = z.infer<typeof swapKindSchema>;

export const SWAP_KIND_LABEL: Record<SwapKind, string> = {
  GIVE_AWAY: 'Ceder a escala',
  EXCHANGE: 'Trocar de lugar',
};

export const swapRequestSchema = z.object({
  id: z.string().uuid(),
  kind: swapKindSchema,
  status: swapStatusSchema,
  assignmentId: z.string().uuid(),
  eventTitle: z.string(),
  localDate: z.string(),
  localTime: z.string(),
  functionName: z.string(),
  requestedById: z.string().uuid(),
  requestedByName: z.string(),
  invitedPersonName: z.string().nullable(),
  acceptedByName: z.string().nullable(),
  /** Na troca de lugares, o que o colega devolve. */
  emTrocaDe: z
    .object({ eventTitle: z.string(), localDate: z.string(), functionName: z.string() })
    .nullable(),
  reason: z.string().nullable(),
  message: z.string().nullable(),
  decisionComment: z.string().nullable(),
  expiresAt: z.string().datetime(),
  createdAt: z.string().datetime(),
});
export type SwapRequest = z.infer<typeof swapRequestSchema>;

export const createSwapSchema = z.object({
  assignmentId: z.string().uuid('Escolha a escala'),
  kind: swapKindSchema.default('GIVE_AWAY'),
  /** Convite direto a um colega; vazio abre para quem estiver habilitado. */
  invitedPersonId: z.string().uuid().nullish(),
  /** Na troca de lugares, a escala do colega que entra no negócio. */
  targetAssignmentId: z.string().uuid().nullish(),
  reason: z.string().trim().max(300).nullish(),
  message: z.string().trim().max(300).nullish(),
});
export type CreateSwapInput = z.infer<typeof createSwapSchema>;

export const respondSwapSchema = z.object({
  resposta: z.enum(['ACEITAR', 'RECUSAR']),
  /** Na troca de lugares, a escala que quem aceita oferece em troca. */
  targetAssignmentId: z.string().uuid().nullish(),
});
export type RespondSwapInput = z.infer<typeof respondSwapSchema>;

export const decideSwapSchema = z.object({
  decisao: z.enum(['APROVAR', 'RECUSAR']),
  comentario: z.string().trim().max(300).nullish(),
});
export type DecideSwapInput = z.infer<typeof decideSwapSchema>;

/** Colega que pode assumir a escala, para a tela de pedido de troca. */
export const swapCandidateSchema = z.object({
  personId: z.string().uuid(),
  nome: z.string(),
  fotoUrl: z.string().nullable(),
  elegivel: z.boolean(),
  motivo: z.string().nullable(),
  escalasNoMes: z.number().int(),
});
export type SwapCandidate = z.infer<typeof swapCandidateSchema>;

// ── Dia do evento ────────────────────────────────────────────────────────────

export const CHECKIN_METHOD = ['QR', 'LEADER', 'SELF'] as const;
export const checkinMethodSchema = z.enum(CHECKIN_METHOD);
export type CheckinMethod = z.infer<typeof checkinMethodSchema>;

/** Código do QR da recepção: gira sozinho, para foto compartilhada não valer. */
export const checkinCodeSchema = z.object({
  code: z.string(),
  eventId: z.string().uuid(),
  expiresAt: z.string().datetime(),
  /** Endereço que o QR carrega. */
  url: z.string(),
});
export type CheckinCode = z.infer<typeof checkinCodeSchema>;

export const checkinByCodeSchema = z.object({
  code: z.string().trim().min(4).max(16),
});
export type CheckinByCodeInput = z.infer<typeof checkinByCodeSchema>;

export const markAttendanceSchema = z.object({
  presente: z.boolean(),
  note: z.string().trim().max(300).nullish(),
});
export type MarkAttendanceInput = z.infer<typeof markAttendanceSchema>;

/** Painel do dia: quem serve, quem confirmou, quem chegou (M09). */
export const dayPanelSchema = z.object({
  event: z.object({
    id: z.string().uuid(),
    title: z.string(),
    localDate: z.string(),
    localTime: z.string(),
    location: z.string().nullable(),
    status: z.string(),
  }),
  resumo: z.object({
    escalados: z.number().int(),
    confirmados: z.number().int(),
    presentes: z.number().int(),
    ausentes: z.number().int(),
    faltando: z.number().int(),
  }),
  departamentos: z.array(
    z.object({
      ministryId: z.string().uuid(),
      ministryName: z.string(),
      ministryColor: z.string(),
      pessoas: z.array(
        z.object({
          assignmentId: z.string().uuid(),
          personId: z.string().uuid(),
          personName: z.string(),
          photoUrl: z.string().nullable(),
          functionName: z.string(),
          status: assignmentStatusSchema,
          checkedInAt: z.string().datetime().nullable(),
          method: checkinMethodSchema.nullable(),
        }),
      ),
    }),
  ),
  /** Vagas essenciais que ninguém ocupou — o que o líder precisa resolver hoje. */
  emAberto: z.array(z.object({ functionName: z.string(), ministryName: z.string(), quantidade: z.number().int() })),
});
export type DayPanel = z.infer<typeof dayPanelSchema>;

/** Confirmação por link mágico, sem login (M08). */
export const magicConfirmSchema = z.object({
  token: z.string().min(16),
  resposta: z.enum(['CONFIRMAR', 'RECUSAR']),
  motivo: declineReasonSchema.optional(),
});
export type MagicConfirmInput = z.infer<typeof magicConfirmSchema>;

export const magicAssignmentSchema = z.object({
  churchName: z.string(),
  personName: z.string(),
  eventTitle: z.string(),
  localDate: z.string(),
  localTime: z.string(),
  functionName: z.string(),
  status: assignmentStatusSchema,
  jaRespondida: z.boolean(),
});
export type MagicAssignment = z.infer<typeof magicAssignmentSchema>;
